import { enforce, omitWhen, test } from 'vest';

import {
  isEndpointUrl,
  withDefaults,
} from 'admin-panel/utils/integrations/config';
import { MENU_LANGUAGE } from 'admin-panel/utils/integrations/service-forms/simphony-gen1';

import type { IntegrationConfig } from 'admin-panel/models/integrations/configuration';
import type {
  IntegrationFormValues,
  ServiceFormDefinition,
} from 'admin-panel/utils/integrations/types';

export interface SimphonyGen2Service {
  authEndpointUrl: string;
  apiEndpointUrl: string;
  clientId: string;
  username: string;
  password: string;
  organisationName: string;
  locationRef: string;
  menuLanguage: string;
  employeeRef: number | null;
  orderTypeRef: number | null;
  tenderMediaRef: number | null;
  revenueCenterRefs: (number | null)[];
}

const SERVICE = 'currentConfig.service';

const isRef = (value: number | null) => Number.isInteger(value);

const defaultConfig = (): IntegrationConfig => ({
  service: {
    authEndpointUrl: '',
    apiEndpointUrl: '',
    clientId: '',
    username: '',
    password: '',
    organisationName: '',
    locationRef: '',
    menuLanguage: '',
    employeeRef: null,
    orderTypeRef: null,
    tenderMediaRef: null,
    revenueCenterRefs: [],
  },
  features: {},
  cron: [],
  webhooks: [],
});

export const simphonyGen2: ServiceFormDefinition = {
  toFormConfig: (config) => withDefaults(defaultConfig(), config),

  validate(values: IntegrationFormValues, t) {
    const service = values.currentConfig
      .service as unknown as SimphonyGen2Service;

    for (const field of ['authEndpointUrl', 'apiEndpointUrl'] as const) {
      test(`${SERVICE}.${field}`, t('validations.required'), () => {
        enforce(service[field]).isNotBlank();
      });

      omitWhen(!service[field], () => {
        test(`${SERVICE}.${field}`, t('validations.url'), () => {
          enforce(isEndpointUrl(service[field])).isTruthy();
        });
      });
    }

    for (const field of [
      'clientId',
      'username',
      'password',
      'organisationName',
      'locationRef',
    ] as const) {
      test(`${SERVICE}.${field}`, t('validations.required'), () => {
        enforce(service[field]).isNotBlank();
      });
    }

    test(`${SERVICE}.menuLanguage`, t('validations.required'), () => {
      enforce(service.menuLanguage).isNotBlank();
    });

    omitWhen(!service.menuLanguage, () => {
      test(`${SERVICE}.menuLanguage`, t('validations.invalid'), () => {
        enforce(service.menuLanguage).matches(MENU_LANGUAGE);
      });
    });

    for (const field of [
      'employeeRef',
      'orderTypeRef',
      'tenderMediaRef',
    ] as const) {
      test(`${SERVICE}.${field}`, t('validations.required'), () => {
        enforce(isRef(service[field])).isTruthy();
      });
    }

    test(`${SERVICE}.revenueCenterRefs`, t('validations.required'), () => {
      const refs = Array.isArray(service.revenueCenterRefs)
        ? service.revenueCenterRefs
        : [];

      enforce(refs).isNotEmpty();
      enforce(refs.every(isRef)).isTruthy();
    });
  },
};
